import i18nFactory from '@yandex-int/i18n';
import { isEqual, union } from 'lodash';

import { LogHelpers } from '../../../service/LogHelpers/LogHelpers';
import {
    CampaignDayBudgetAutoProlongationParam,
    CampaignDayBudgetDateParam,
    CampaignDayBudgetModeParam,
    CampaignDayBudgetSumParam,
    CampaignStrategyClicksLimitParam,
    CampaignStrategyGoalParam,
    CampaignStrategyParam,
    CampaignStrategyProfitabilityParam,
    CampaignStrategyReserveReturnParam,
    CampaignStrategyRoiCoefParam,
    CampaignStrategySeparateBidParam,
    StrategyHelpers
} from '../../../service/StrategyHelpers/StrategyHelpers';
import {
    CurrencyCode,
    GdCampaignBudgetPeriod,
    GdCampaignBudgetShowMode,
    GdCampaignPlatform
} from '../../../typings/graphql-grid';
import { FlatStrategyFragment, GdStrategyType } from '../../../typings/graphql-log';
import {
    CampaignDayBudgetDiff,
    CampaignStrategyParamsDiff,
    ChangedOptions,
    StrategyGroups,
    StrategyValue
} from '../../../typings/log';
import { ClientFeatures } from '../../../typings/state';
import {
    LogCampaignDayBudget,
    LogCampaignStrategyParams,
    LogCampaignStrategyParamValue,
    OptimizationStrategyParamNames,
    StrategyBudgetParamNames,
    StrategyParamNames
} from '../../../typings/strategy';
import { getValueFromApiFormat } from '../../common/common';

import * as keyset from './strategy.i18n';

const i18n = i18nFactory(keyset);

type valuesTypes =
    | GdStrategyType
    | GdCampaignPlatform
    | CampaignStrategyParam
    | GdCampaignBudgetPeriod
    | string
    | number
    | boolean
    | null;

export function prepareStrategy(
    features: ClientFeatures,
    before?: FlatStrategyFragment | null,
    after?: FlatStrategyFragment | null,
    currency?: CurrencyCode
): StrategyGroups {
    const beforeMapped = before
            ? {
                  currency: currency || CurrencyCode.RUB,
                  type: before.strategyType,
                  platform: before.platform,
                  param: mapValues<LogCampaignStrategyParams>(before),
                  budget: mapValues<LogCampaignDayBudget>(before.budget)
              }
            : undefined,
        patchedBefore = patchWeekBudget(beforeMapped);

    const afterMapped = after
            ? {
                  currency: currency || CurrencyCode.RUB,
                  type: after.strategyType,
                  platform: after.platform,
                  param: mapValues<LogCampaignStrategyParams>(after),
                  budget: mapValues<LogCampaignDayBudget>(after.budget)
              }
            : undefined,
        patchedAfter = patchWeekBudget(afterMapped);

    if (!patchedAfter) {
        throw Error('Некорректное значение стратегий: нет данных о текущей стратегии');
    }

    return prepareDiff(features, patchedAfter, patchedBefore);
}

function mapValues<T>(values: {} | null): T | null {
    if (values === null) {
        return null;
    }

    return Object.keys(values).reduce(
        (res: T, key: string) => {
            const value = values[key];

            switch (key) {
                case StrategyParamNames.goal:
                    res[StrategyParamNames.goal] = {
                        ...value,
                        id: getValueFromApiFormat(value.id)
                    };
                    return res;
                case StrategyParamNames.bid:
                case StrategyParamNames.avgCpa:
                case StrategyParamNames.avgCpm:
                case StrategyParamNames.profitability:
                case StrategyParamNames.roiCoef:
                case StrategyParamNames.reserveReturn:
                case StrategyParamNames.clicksLimit:
                case StrategyParamNames.filterAvgCpa:
                case StrategyParamNames.filterAvgBid:
                case StrategyBudgetParamNames.sum:
                    res[StrategyParamNames[key]] = getValueFromApiFormat(value);
                    return res;
                case StrategyParamNames.separateBidding:
                case StrategyBudgetParamNames.autoProlongation:
                case StrategyBudgetParamNames.start:
                case StrategyBudgetParamNames.finish:
                    res[key] = getValueFromApiFormat(value);
                    return res;
                default:
                    res[key] = value;
                    return res;
            }
        },
        {} as T
    );
}

// для недельного бюджета параметры приходят в поле budget
// но пользователь интерфейса привык, что это настройка стратегии, а не бюджет
function patchWeekBudget(value?: StrategyValue): StrategyValue | undefined {
    if (!value) {
        return value;
    }

    if (value && value.budget && value.budget.period === GdCampaignBudgetPeriod.WEEK) {
        return {
            ...value,
            param: { ...value.param, sum: value.budget.sum },
            budget: null
        };
    }

    return value;
}

function prepareDiff(features: ClientFeatures, after: StrategyValue, before?: StrategyValue): StrategyGroups {
    const params = prepareStrategyParam(features, after, before);
    const budget = prepareStrategyBudget(after, before);

    const oldName = before ? before.type : undefined;
    const oldPlatform = before ? before.platform : undefined;
    const { type: newName, platform: newPlatform } = after;

    const name = {
        oldName,
        newName,
        type: countChangedOption(oldName, newName, true)
    };
    const platform = {
        oldPlatform,
        newPlatform,
        type: countChangedOption(oldPlatform, newPlatform)
    };
    const afterParams = filterAfterParams(params, Object.keys(after.param || {}));
    const afterBudget = filterAfterParams(budget, Object.keys(after.budget || {}));

    const alwaysNeedExpand = calcAlwaysNeedExpand({
        newName,
        oldName,
        newPlatform,
        oldPlatform,
        params: name.type === ChangedOptions.notModified ? params : afterParams,
        budget: name.type === ChangedOptions.notModified ? budget : afterBudget
    });
    const maxLineLength = calcMaxLineLength(
        {
            newName,
            oldName,
            newPlatform,
            oldPlatform,
            params: name.type === ChangedOptions.notModified ? params : afterParams,
            budget: name.type === ChangedOptions.notModified ? budget : afterBudget
        },
        features
    );

    return {
        params,
        budget,
        name,
        platform,
        alwaysNeedExpand,
        maxLineLength,
        needExpand: LogHelpers.calcNeedExpand(alwaysNeedExpand, maxLineLength)
    };
}

function prepareStrategyParam(
    features: ClientFeatures,
    after: StrategyValue,
    before?: StrategyValue
): CampaignStrategyParamsDiff {
    const currency = before ? before.currency : after.currency,
        beforeParam = before && before.param !== null ? before.param : {},
        afterParam = after.param || {},
        oldParamsKeys = Object.keys(beforeParam).filter(filterParams),
        newParamsKeys = Object.keys(afterParam).filter(filterParams),
        allParams = union(oldParamsKeys, newParamsKeys) as Array<keyof LogCampaignStrategyParams>;

    return allParams.reduce((res: CampaignStrategyParamsDiff, paramName: string) => {
        const oldValue = beforeParam[paramName] || null,
            oldClass = createStrategyParamClass(
                oldValue,
                paramName as keyof LogCampaignStrategyParams,
                features,
                before ? before.type : GdStrategyType.DEFAULT,
                currency
            ),
            newValue = afterParam[paramName] || null,
            newClass = createStrategyParamClass(
                newValue,
                paramName as keyof LogCampaignStrategyParams,
                features,
                after.type,
                currency
            );

        // с бэкэнда всегда приходит полный список параметров
        // если настройка не выставлена - то она null
        // фильтруем параметры, которые не выставлены ни в "предыдущей", ни в "текущей" стратегии
        if (oldValue !== null || newValue !== null) {
            res[paramName] = {
                oldValue: oldClass,
                newValue: newClass,
                type: countChangedOption(oldValue, newValue)
            };
        }

        return res;
    }, {});
}

function prepareStrategyBudget(after: StrategyValue, before?: StrategyValue): CampaignDayBudgetDiff {
    const currency = before ? before.currency : after.currency,
        beforeBudget = before && before.budget !== null ? before.budget : {},
        afterBudget = after.budget || {},
        oldBudgetKeys = Object.keys(beforeBudget).filter(filterParams),
        newBudgetKeys = Object.keys(afterBudget).filter(filterParams),
        allBudget = union(oldBudgetKeys, newBudgetKeys) as Array<keyof LogCampaignDayBudget>;

    return allBudget.reduce((res: CampaignDayBudgetDiff, paramName: string) => {
        const oldValue = beforeBudget[paramName] || null,
            oldClass = createStrategyBudgetClass(
                oldValue,
                paramName as keyof LogCampaignDayBudget,
                before ? before.type : GdStrategyType.DEFAULT,
                currency
            ),
            newValue = afterBudget[paramName] || null,
            newClass = createStrategyBudgetClass(
                newValue,
                paramName as keyof LogCampaignDayBudget,
                after.type,
                currency
            );

        // с бэкэнда всегда приходит полный список параметров
        // если настройка не выставлена - то она null
        // фильтруем параметры, которые не выставлены ни в "предыдущей", ни в "текущей" стратегии
        if ((oldClass !== null || newClass !== null) && (oldValue !== null || newValue !== null)) {
            res[paramName] = {
                oldValue: oldClass,
                newValue: newClass,
                type: countChangedOption(oldValue, newValue)
            };
        }

        return res;
    }, {});
}

// в списке параметров есть еще сервисное поле __typename
function filterParams(key: string): boolean {
    return key in StrategyParamNames || key in StrategyBudgetParamNames;
}

function createStrategyParamClass(
    val: LogCampaignStrategyParamValue,
    key: keyof LogCampaignStrategyParams,
    features: ClientFeatures,
    name?: GdStrategyType,
    currency?: CurrencyCode
): CampaignStrategyParam {
    if (val === null) {
        return val;
    }

    switch (key) {
        case StrategyParamNames.avgBid:
        case StrategyParamNames.avgCpa:
            return new CampaignStrategyParam(
                val,
                StrategyHelpers.isOptimizationCampaign(name) ? (`${key}Opt` as OptimizationStrategyParamNames) : key,
                currency,
                features
            );
        case StrategyParamNames.clicksLimit:
            return new CampaignStrategyClicksLimitParam(val, key);
        case StrategyParamNames.roiCoef:
            return new CampaignStrategyRoiCoefParam(val, key);
        case StrategyParamNames.reserveReturn:
            return new CampaignStrategyReserveReturnParam(val, key);
        case StrategyParamNames.profitability:
            return new CampaignStrategyProfitabilityParam(val, key);
        case StrategyParamNames.separateBidding:
            return new CampaignStrategySeparateBidParam(val, key);
        case StrategyParamNames.goal:
            return new CampaignStrategyGoalParam(val, name || '');
        default:
            return new CampaignStrategyParam(val, key, currency, features);
    }
}

function createStrategyBudgetClass(
    val: LogCampaignStrategyParamValue,
    key: keyof LogCampaignDayBudget,
    name?: GdStrategyType,
    currency?: CurrencyCode
): CampaignStrategyParam | GdCampaignBudgetPeriod | null {
    if (val === null) {
        return val;
    }

    switch (key) {
        case StrategyBudgetParamNames.period:
            return val as GdCampaignBudgetPeriod;
        case StrategyBudgetParamNames.showMode:
            return new CampaignDayBudgetModeParam(val as GdCampaignBudgetShowMode, key);
        case StrategyBudgetParamNames.sum:
            return new CampaignDayBudgetSumParam(val, key, currency);
        case StrategyBudgetParamNames.autoProlongation:
            return new CampaignDayBudgetAutoProlongationParam(val, key);
        case StrategyBudgetParamNames.start:
        case StrategyBudgetParamNames.finish:
            return new CampaignDayBudgetDateParam(val, key);
        default:
            return new CampaignStrategyParam(val, key, currency);
    }
}

function countChangedOption(
    oldValue: valuesTypes = null,
    newValue: valuesTypes = null,
    isName: boolean = false
): ChangedOptions {
    const isEqualValue = isName
        ? checkNamesAreEqual(oldValue as GdStrategyType, newValue as GdStrategyType)
        : isEqual(oldValue, newValue);
    return isEqualValue
        ? ChangedOptions.notModified
        : oldValue === null
        ? ChangedOptions.added
        : newValue === null
        ? ChangedOptions.removed
        : ChangedOptions.modified;
}

function checkNamesAreEqual(oldName: GdStrategyType, newName: GdStrategyType): boolean {
    const maxReachStrategies = [GdStrategyType.CPM_MAX_REACH, GdStrategyType.CPM_MAX_REACH_CUSTOM_PERIOD],
        maxImpStrategies = [GdStrategyType.CPM_MAX_IMPRESSIONS, GdStrategyType.CPM_MAX_IMPRESSIONS_CUSTOM_PERIOD];

    // maxReachStrategies и maxImpStrategies разные с ТЗ бэка, но для пользователя - это 1 стратегия
    // поэтому эти 2 ключа считаются одинаковыми
    return (
        oldName === newName ||
        (maxReachStrategies.includes(oldName) && maxReachStrategies.includes(newName)) ||
        (maxImpStrategies.includes(oldName) && maxImpStrategies.includes(newName))
    );
}

function calcAlwaysNeedExpand(data: {
    oldName?: GdStrategyType;
    newName: GdStrategyType;
    oldPlatform?: GdCampaignPlatform;
    newPlatform: GdCampaignPlatform;
    params: CampaignStrategyParamsDiff;
    budget: CampaignDayBudgetDiff;
}): boolean {
    const { params, budget } = data,
        paramsKeys = Object.keys(params),
        budgetKeys = Object.keys(budget),
        paramsCount = paramsKeys.length,
        budgetCount = budgetKeys.length;

    // самый тривиальный вариант - много параметров или бюджета
    // у бюджета параметр period не считаем - он всегда есть и отображается в заголовке
    if (paramsCount >= 2 || budgetCount >= 3) {
        return true;
    }
    // по 1 параметру в бюджете и параметрах
    return paramsCount === 1 && budgetCount === 1;
}

function calcMaxLineLength(
    data: {
        oldName?: GdStrategyType;
        newName: GdStrategyType;
        oldPlatform?: GdCampaignPlatform;
        newPlatform: GdCampaignPlatform;
        params: CampaignStrategyParamsDiff;
        budget: CampaignDayBudgetDiff;
    },
    features: ClientFeatures
): number {
    const { oldName, newName, oldPlatform, newPlatform, params, budget } = data,
        newNameLength = (StrategyHelpers.getTitle(features, newName, newPlatform) || '').length,
        oldNameLength = (StrategyHelpers.getTitle(features, oldName, oldPlatform) || '').length,
        newPlatformLength = (StrategyHelpers.getPlatformName(newPlatform) || '').length,
        oldPlatformLength = (StrategyHelpers.getPlatformName(oldPlatform) || '').length,
        isNew = oldName === null,
        paramsKeys = Object.keys(params),
        budgetKeys = Object.keys(budget),
        paramsCount = paramsKeys.length,
        budgetCount = budgetKeys.length;

    let strategyNameLength,
        platfromNameLength,
        paramNameLength = 0,
        budgetNameLength = 0;

    // название стратегий длиннее 1 строки
    // + 1 - это пробел
    const nameLength = oldName === newName || !oldName ? newNameLength : oldNameLength + newNameLength + 1,
        // +2 - двоеточие + пробел
        strategyWordLength = i18n('strategy').length + 2;
    strategyNameLength = nameLength + strategyWordLength;

    // название платформ длиннее 1 строки
    // + 1 - это пробел
    const platformLenth =
            oldPlatform === newPlatform || !oldPlatform ? newPlatformLength : oldPlatformLength + newPlatformLength + 1,
        // +2 - двоеточие + пробел
        platformWordLength = i18n('platform').length + 2;
    platfromNameLength = platformLenth + platformWordLength;

    if (paramsCount === 1) {
        const paramOldValue = params[paramsKeys[0]].oldValue,
            paramNewValue = params[paramsKeys[0]].newValue;
        if (isNew && paramNewValue === null) {
            paramNameLength = 0;
        } else {
            if (paramNewValue === null) {
                paramNameLength =
                    paramOldValue.getValueText().length +
                    (paramOldValue.getHasDash() ? 1 : 3) +
                    paramOldValue.getParamText().length +
                    1;
            } else {
                const paramLength =
                    paramNewValue.getValueText().length +
                    (paramNewValue.getHasDash() ? 1 : 3) +
                    paramNewValue.getParamText().length +
                    1; // +1 - отступ слева

                if (paramOldValue === null) {
                    paramNameLength = paramLength;
                } else {
                    paramNameLength =
                        // +1 - это пробел
                        paramLength + 1 + paramOldValue.getValueText().length;
                }
            }
        }
    }

    // проверяем случай: 1 параметр бюджета длиннее строки
    if (budgetCount === 1) {
        const budgetOldValue = budget[budgetKeys[0]].oldValue,
            budgetNewValue = budget[budgetKeys[0]].newValue;
        if (isNew && budgetNewValue === null) {
            budgetNameLength = 0;
        } else {
            if (budgetNewValue === null) {
                budgetNameLength =
                    budgetOldValue.getValueText().length +
                    (budgetOldValue.getHasDash() ? 3 : 1) +
                    budgetOldValue.getParamText().length +
                    1;
            } else {
                const budgetLength =
                    budgetNewValue.getValueText().length +
                    (budgetNewValue.getHasDash() ? 3 : 1) +
                    budgetNewValue.getParamText().length +
                    1; // +1 - отступ слева

                if (budgetOldValue === null) {
                    budgetNameLength = budgetLength;
                } else {
                    budgetNameLength =
                        // +1 - это пробел
                        budgetLength + 1 + budgetOldValue.getValueText().length;
                }
            }
        }
    }

    return Math.max(strategyNameLength, platfromNameLength, paramNameLength, budgetNameLength);
}

function filterAfterParams<T>(params: T, filterKeys: Array<string>): T {
    const paramsKeys = Object.keys(params);
    return filterKeys.reduce(
        (res: T, key: string) => {
            if (paramsKeys.includes(key)) {
                res[key] = params[key];
            }
            return res;
        },
        {} as T
    );
}
